import styled from 'styled-components';

// Cores accent — UCHAs=orange, EBAs=blue. Match exato com tokens CCO.
const accent = (props) =>
  props.color === 'blue' ? 'var(--cco-blue)' : 'var(--cco-orange)';
const accentSoft = (props) =>
  props.color === 'blue' ? 'var(--cco-blue-soft)' : 'var(--cco-orange-soft)';
const accentBorder = (props) =>
  props.color === 'blue'
    ? 'rgba(78, 161, 243, 0.25)'
    : 'rgba(241, 162, 91, 0.25)';

// =============================================================================
// Stat card root — match .stat do Dashboard.html
// grid 3-row x 2-col: title full-width / donut left rowspan 2 / agora top-right
// / energy bottom-right. Hairlines dividem regioes internas.
// =============================================================================
export const Container = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  grid-template-columns: 1.05fr 1fr;
  grid-template-rows: auto 1fr auto;
  min-width: 0;
  background: transparent;
  color: var(--cco-ink);
`;

// === Title bar full-width === //
export const TagTitle = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
`;

export const TitleChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 999px;
  background: ${accentSoft};
  color: ${accent};
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: inset 0 0 0 1px ${accentBorder};
  white-space: nowrap;
`;

export const TitleMeta = styled.span`
  color: var(--cco-ink-mute);
  font-size: 12.5px;

  b {
    color: var(--cco-ink);
    font-weight: 600;
  }
`;

// === Coluna esquerda (donut) — span row 2/3 === //
export const Column = styled.div`
  ${(props) =>
    props.$stretch
      ? `
        grid-column: 1;
        grid-row: 2 / span 2;
        padding: 16px;
        display: grid;
        place-items: center;
        position: relative;
        border-right: 1px solid var(--cco-line);
        min-width: 0;

        @media (min-width: 1400px) {
          padding: 24px;
        }
      `
      : `
        grid-column: 2;
        grid-row: 2 / span 2;
        display: flex;
        flex-direction: column;
        min-width: 0;
      `}
`;

// === Donut + center text. Tamanho responsivo via clamp pra caber em
// telas estreitas (laptops 1366) sem quebrar layout. === //
export const GraphContainer = styled.div`
  position: relative;
  width: clamp(160px, 18vw, 230px);
  height: clamp(160px, 18vw, 230px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
`;

export const GraphAbsoluteText = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  pointer-events: none;
  color: var(--cco-ink-mute);
  font-size: clamp(10px, 0.85vw, 12px);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 500;

  span {
    display: block;
    font-size: clamp(30px, 3vw, 44px);
    line-height: 1;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--cco-ink);
    margin-bottom: 6px;
    font-variant-numeric: tabular-nums;
  }
`;

// Legend tag (numero colorido posicionado ao redor do donut)
export const LegTag = styled.span`
  position: absolute;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.tagColor};
`;

// === Section label (Agora, Energia gerada/consumida) === //
export const SectionLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--cco-ink-mute);
  font-weight: 600;
  text-align: center;
`;

// === Bloco Agora (Potencia | sep | Vazao) === //
export const LiveCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px var(--cco-pad) 18px;
  border-bottom: 1px solid var(--cco-line);
`;

export const LivePair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 0;
  align-items: stretch;
`;

// Divisor vertical 1px entre Potencia e Vazao — elemento explicito
// (em vez de ::before pseudo) pra garantir ordem correta de
// auto-flow do grid: KPI | Sep | KPI cada um em sua coluna.
export const LivePairSep = styled.span`
  background: var(--cco-line);
  width: 1px;
  height: 100%;
`;

export const LiveMetric = styled.div`
  padding: 6px 12px;
  text-align: center;
  min-width: 0;
`;

export const LiveMetricHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${accent};

  > svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
    flex-shrink: 0;
  }
`;

export const LiveMetricValue = styled.div`
  margin-top: 6px;
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  color: ${(props) => {
    /* $signed: usa verde/vermelho (convencao gerar=verde, consumir=
       vermelho). Sem $signed: usa accent (orange/blue por tipo). */
    if (props.$signed) {
      return props.negative ? 'var(--cco-red)' : 'var(--cco-green)';
    }
    return accent(props);
  }};

  span,
  small {
    font-size: 14px;
    color: var(--cco-ink-mute);
    font-weight: 500;
    margin-left: 4px;
  }
`;

// === Bloco Energia (tabs + valor hero + footer acumulado) === //
export const EnergyBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px var(--cco-pad) 22px;
`;

export const SegmentedSelector = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px;
  gap: 2px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--cco-line);
`;

export const SegmentedOption = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: ${(props) =>
    props.selected ? accent(props) : 'transparent'};
  color: ${(props) => {
    if (!props.selected) return 'var(--cco-ink-mute)';
    return props.color === 'blue' ? '#fff' : '#1a1208';
  }};
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 150ms, color 150ms;

  &:hover {
    color: ${(props) =>
      props.selected
        ? props.color === 'blue'
          ? '#fff'
          : '#1a1208'
        : accent(props)};
  }
`;

export const HeroPeriodValue = styled.div`
  font-size: 34px;
  font-weight: 600;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.negative ? 'var(--cco-red)' : 'var(--cco-green)'};
  line-height: 1.1;

  small,
  span {
    font-size: 18px;
    color: var(--cco-ink-mute);
    margin-left: 4px;
    font-weight: 500;
  }
`;

export const FooterMeta = styled.div`
  margin-top: 10px;
  padding-top: 14px;
  border-top: 1px solid var(--cco-line);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12.5px;
  color: var(--cco-ink-mute);
`;

export const FooterLabel = styled.span``;

export const FooterValue = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${(props) =>
    props.negative ? 'var(--cco-red)' : 'var(--cco-green)'};
`;

// === Estados de loading/empty/timeout (preservados) === //
export const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const EmptyContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 24px;
  text-align: center;
`;

export const EmptyMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--cco-ink-mute);
  max-width: 280px;
  line-height: 1.4;
`;

export const TimeoutContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  gap: 12px;
  color: var(--cco-ink-mute);
`;

export const TimeoutTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: var(--cco-ink);
`;

export const TimeoutMessage = styled.p`
  margin: 0;
  font-size: 13px;
  max-width: 280px;
  line-height: 1.4;
`;

export const RetryButton = styled.button`
  margin-top: 4px;
  padding: 6px 16px;
  border: 1px solid var(--cco-blue);
  border-radius: 6px;
  background: transparent;
  color: var(--cco-blue);
  font-weight: bold;
  font-size: 13px;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: var(--cco-blue);
    color: #fff;
  }
`;
